import { ArtifactProto } from "build-ts/chat_session";
import showToast from "hooks/useCustomToast";
import { KGObservationProto } from "build-ts/analysis_plan";
import { GetMKGObservationData_Request, GetMKGObservationData_Response } from "build-ts/interface";
import { triggerDownload } from "utils/helperFuncUtils";
import { ReportDownloadFormat } from "./serviceUtils";
import { getMkgObservationDataUrl } from "./newtonProperties";
import { getDownloadReportUrl, getPopulateArtifactUrl } from "./darwinProperties";

export const populateArtifact = async (request: ArtifactProto) => {
  try {
    const response = await fetch(getPopulateArtifactUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(ArtifactProto.toJSON(request)),
      credentials: "include",
    });
    if (!response.ok) {
      showToast("Error populating artifact", "", "error");
      return null;
    }

    const responseData = await response.json();
    return ArtifactProto.fromJSON(responseData);
  } catch (error) {
    showToast("Error populating artifact", "", "error");
    return null;
  }
};

/**
 * Fetches KGObservation data for the given KG observation.
 *
 * @param kgObservations - The KG observations to fetch data for.
 * @returns {Promise<GetMKGObservationData_Response[]>} A promise that resolves to the fetched KGObservation data.
 * @throws {Error} Throws an error if there is an issue fetching the KGObservation data or if the request fails.
 */
export const getKGObservationData = async (kgObservations: KGObservationProto[]) => {
  try {
    const requests = kgObservations.map(kgObservation =>
      GetMKGObservationData_Request.create({ kg_observation: kgObservation })
    );
    const response = await fetch(getMkgObservationDataUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(requests.map(request => GetMKGObservationData_Request.toJSON(request))),
    });
    if (!response.ok) {
      showToast("KGObservation Data Fetch Failed", "", "error");
      return null;
    }
    const data = await response.json();
    return data.map((response: GetMKGObservationData_Response) =>
      GetMKGObservationData_Response.fromJSON(response)
    ) as GetMKGObservationData_Response[];
  } catch (error) {
    showToast("Error in fetching KGObservation data", "", "error");
    return null;
  }
};

/**
 * Fetch a report artifact as a binary file (DOCX or PDF) and trigger a browser
 * download. The backend regenerates the file on demand from the persisted LLM
 * code each time — DOCX/PDF bytes are never stored in the DB. For PDFs, the
 * backend also runs the LibreOffice conversion step server-side, so the UI
 * never needs to know about soffice.
 *
 * Returns true on success, false if the download failed (toast is shown).
 */
export const downloadReportFile = async (
  chatId: string,
  artifactId: string,
  format: ReportDownloadFormat,
  name?: string,
  reportName?: string
): Promise<boolean> => {
  try {
    const params = new URLSearchParams({
      chat_id: chatId,
      artifact_id: artifactId,
      format,
    });
    if (reportName) params.set("report_name", reportName);
    const response = await fetch(`${getDownloadReportUrl()}?${params.toString()}`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      showToast(`Error downloading report (${format.toUpperCase()})`, "", "error");
      return false;
    }

    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition");
    const match = disposition?.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `${name ?? "report"}.${format}`;
    const url = URL.createObjectURL(blob);
    triggerDownload(url, filename);
    return true;
  } catch (error) {
    showToast(`Error downloading report (${format.toUpperCase()})`, "", "error");
    return false;
  }
};
